import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { api } from '../api/client';
import type { HubSpotContact } from '@aigftpwatch/shared';

interface HubSpotSearchResult {
  success: boolean;
  data: HubSpotContact[];
  total: number;
}

/**
 * Debounced HubSpot contact search hook.
 * Waits for the specified debounce period before firing the API call.
 * Requires at least 2 characters to trigger a search.
 */
export function useHubSpotSearch(query: string, debounceMs = 350) {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), debounceMs);
    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  const trimmed = debouncedQuery.trim();
  const enabled = trimmed.length >= 2;

  return useQuery({
    queryKey: ['hubspot-contacts', trimmed],
    queryFn: () =>
      api.getRaw<HubSpotSearchResult>(
        `/hubspot/contacts/search?q=${encodeURIComponent(trimmed)}&limit=10`,
      ),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    select: (res) => ({ contacts: res.data, total: res.total }),
  });
}
